import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Salesforce Logo component
const SalesforceLogo = () => (
  <img
    src="https://c1.sfdcstatic.com/content/dam/sfdc-docs/www/logos/logo-salesforce.svg"
    alt="Salesforce"
    style={{ width: 100, height: 40, marginRight: '1rem' }}
    onError={(e) => {
      e.target.onerror = null;
      e.target.style.display = 'none';
    }}
  />
);

// Direction icons
const DirectionIcon = ({ direction }) => {
  switch (direction) {
    case 'bidirectional':
      return <span style={{ fontSize: '1.25rem' }}>⇄</span>;
    case 'to_salesforce':
      return <span style={{ fontSize: '1.25rem' }}>→</span>;
    case 'from_salesforce':
      return <span style={{ fontSize: '1.25rem' }}>←</span>;
    default:
      return <span style={{ fontSize: '1.25rem' }}>⇄</span>;
  }
};

// Object type icons
const getObjectIcon = (type) => {
  switch (type) {
    case 'leads':
    case 'contacts':
      return '👤';
    case 'accounts':
      return '🏢';
    case 'opportunities':
      return '📈';
    default:
      return '📄';
  }
};

// Toast Notification
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'error' ? 'var(--red-600)' : type === 'warning' ? 'var(--yellow-600)' : 'var(--green-600)';

  return (
    <div style={{
      position: 'fixed',
      bottom: '1rem',
      right: '1rem',
      backgroundColor: bgColor,
      color: 'white',
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      zIndex: 1001
    }}>
      <span>{type === 'error' ? '❌' : type === 'warning' ? '⚠️' : '✅'}</span>
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          marginLeft: '0.5rem',
          background: 'none',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '1.25rem'
        }}
      >
        ×
      </button>
    </div>
  );
};

// ============================================================================
// Field Mapping Editor
// ============================================================================

const FieldMappingEditor = ({ mappings, availableFields, salesforceFields, onSave, onCancel }) => {
  const [localMappings, setLocalMappings] = useState(mappings || []);
  const [newMapping, setNewMapping] = useState({
    salesradar: '',
    salesforce: '',
    direction: 'bidirectional'
  });

  const directionOptions = [
    { value: 'bidirectional', label: 'Bidirectional' },
    { value: 'to_salesforce', label: 'To Salesforce Only' },
    { value: 'from_salesforce', label: 'From Salesforce Only' }
  ];

  const handleAddMapping = () => {
    if (newMapping.salesradar && newMapping.salesforce) {
      setLocalMappings([...localMappings, { ...newMapping }]);
      setNewMapping({ salesradar: '', salesforce: '', direction: 'bidirectional' });
    }
  };

  const handleRemoveMapping = (index) => {
    const updated = localMappings.filter((_, i) => i !== index);
    setLocalMappings(updated);
  };

  const handleUpdateMapping = (index, field, value) => {
    const updated = [...localMappings];
    updated[index][field] = value;
    setLocalMappings(updated);
  };

  const tableHeaderStyle = {
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--gray-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid var(--gray-200)'
  };

  const tableCellStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid var(--gray-100)',
    verticalAlign: 'middle'
  };

  const selectStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: 'var(--gray-50)' }}>
            <tr>
              <th style={tableHeaderStyle}>Sales Radar Field</th>
              <th style={tableHeaderStyle}>Direction</th>
              <th style={tableHeaderStyle}>Salesforce Field</th>
              <th style={{ ...tableHeaderStyle, width: 50 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {localMappings.map((mapping, index) => (
              <tr key={index}>
                <td style={tableCellStyle}>
                  <select
                    value={mapping.salesradar}
                    onChange={(e) => handleUpdateMapping(index, 'salesradar', e.target.value)}
                    style={selectStyle}
                  >
                    {availableFields.map(field => (
                      <option key={field} value={field}>{field}</option>
                    ))}
                  </select>
                </td>
                <td style={tableCellStyle}>
                  <select
                    value={mapping.direction}
                    onChange={(e) => handleUpdateMapping(index, 'direction', e.target.value)}
                    style={selectStyle}
                  >
                    {directionOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </td>
                <td style={tableCellStyle}>
                  <select
                    value={mapping.salesforce}
                    onChange={(e) => handleUpdateMapping(index, 'salesforce', e.target.value)}
                    style={selectStyle}
                  >
                    {salesforceFields.map(field => (
                      <option key={field.name} value={field.name}>
                        {field.label || field.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={tableCellStyle}>
                  <button
                    onClick={() => handleRemoveMapping(index)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      border: 'none',
                      backgroundColor: 'var(--red-50)',
                      color: 'var(--red-600)',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {/* New mapping row */}
            <tr style={{ backgroundColor: 'var(--gray-50)' }}>
              <td style={tableCellStyle}>
                <select
                  value={newMapping.salesradar}
                  onChange={(e) => setNewMapping({ ...newMapping, salesradar: e.target.value })}
                  style={selectStyle}
                >
                  <option value="" disabled>Select field...</option>
                  {availableFields.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </td>
              <td style={tableCellStyle}>
                <select
                  value={newMapping.direction}
                  onChange={(e) => setNewMapping({ ...newMapping, direction: e.target.value })}
                  style={selectStyle}
                >
                  {directionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
              <td style={tableCellStyle}>
                <select
                  value={newMapping.salesforce}
                  onChange={(e) => setNewMapping({ ...newMapping, salesforce: e.target.value })}
                  style={selectStyle}
                >
                  <option value="" disabled>Select field...</option>
                  {salesforceFields.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label || field.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={tableCellStyle}>
                <button
                  onClick={handleAddMapping}
                  disabled={!newMapping.salesradar || !newMapping.salesforce}
                  className="btn btn-primary"
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '1rem',
                    opacity: (!newMapping.salesradar || !newMapping.salesforce) ? 0.5 : 1
                  }}
                >
                  ➕
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button 
          className="btn btn-primary" 
          onClick={() => onSave(localMappings)}
          disabled={localMappings.length === 0}
        >
          Save Mappings
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Sync History Component
// ============================================================================

const SyncHistory = ({ history, loading }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'partial':
        return '⚠️';
      default:
        return '🔄';
    }
  };

  const getDirectionLabel = (direction) => {
    switch (direction) {
      case 'to_salesforce':
        return 'To Salesforce';
      case 'from_salesforce':
        return 'From Salesforce';
      case 'bidirectional':
        return 'Bidirectional';
      default:
        return direction;
    }
  };

  const tableHeaderStyle = {
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--gray-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid var(--gray-200)'
  };

  const tableCellStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid var(--gray-100)'
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <span style={{ fontSize: '1.5rem' }}>⏳</span>
        <span style={{ marginLeft: '0.5rem', color: 'var(--gray-600)' }}>Loading...</span>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.3 }}>📋</div>
        <p style={{ color: 'var(--gray-500)' }}>No sync history available</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ backgroundColor: 'var(--gray-50)' }}>
          <tr>
            <th style={tableHeaderStyle}>Time</th>
            <th style={tableHeaderStyle}>Direction</th>
            <th style={tableHeaderStyle}>Object Type</th>
            <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Records</th>
            <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Success</th>
            <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Failed</th>
            <th style={tableHeaderStyle}>Status</th>
          </tr>
        </thead>
        <tbody>
          {history.map((item, index) => (
            <tr key={index}>
              <td style={tableCellStyle}>
                {new Date(item.timestamp).toLocaleString()}
              </td>
              <td style={tableCellStyle}>{getDirectionLabel(item.direction)}</td>
              <td style={tableCellStyle}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '0.25rem'
                }}>
                  {item.object_type}
                </span>
              </td>
              <td style={{ ...tableCellStyle, textAlign: 'right' }}>{item.total_records || 0}</td>
              <td style={{ ...tableCellStyle, textAlign: 'right', color: 'var(--green-600)' }}>
                {item.success_count || 0}
              </td>
              <td style={{ ...tableCellStyle, textAlign: 'right', color: 'var(--red-600)' }}>
                {item.error_count || 0}
              </td>
              <td style={tableCellStyle}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  {getStatusIcon(item.status)}
                  <span style={{ textTransform: 'capitalize' }}>{item.status}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Object Sync Configuration Card
// ============================================================================

const ObjectSyncConfig = ({ config, onChange, objectType }) => {
  const selectStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  };

  return (
    <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.5rem' }}>{getObjectIcon(objectType)}</span>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, textTransform: 'capitalize' }}>
            {objectType} Sync
          </h3>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={config?.enabled || false}
            onChange={(e) => onChange({ ...config, enabled: e.target.checked })}
            style={{ marginRight: '0.5rem', width: '1.25rem', height: '1.25rem' }}
          />
          <span>{config?.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>
      
      {config?.enabled && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                Sync Direction
              </label>
              <select
                value={config?.direction || 'bidirectional'}
                onChange={(e) => onChange({ ...config, direction: e.target.value })}
                style={selectStyle}
              >
                <option value="bidirectional">⇄ Bidirectional</option>
                <option value="to_salesforce">→ To Salesforce Only</option>
                <option value="from_salesforce">← From Salesforce Only</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                Sync Frequency
              </label>
              <select
                value={config?.frequency || 'realtime'}
                onChange={(e) => onChange({ ...config, frequency: e.target.value })}
                style={selectStyle}
              >
                <option value="realtime">Real-time</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config?.create_new || false}
                onChange={(e) => onChange({ ...config, create_new: e.target.checked })}
                style={{ marginRight: '0.5rem', width: '1rem', height: '1rem' }}
              />
              <span style={{ fontSize: '0.875rem' }}>Create new records in Salesforce</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config?.update_existing || false}
                onChange={(e) => onChange({ ...config, update_existing: e.target.checked })}
                style={{ marginRight: '0.5rem', width: '1rem', height: '1rem' }}
              />
              <span style={{ fontSize: '0.875rem' }}>Update existing records</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Connect Modal
// ============================================================================

const ConnectModal = ({ onClose, onConnect }) => {
  const [credentials, setCredentials] = useState({
    client_id: '',
    client_secret: '',
    is_sandbox: false
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ 
        width: '100%', 
        maxWidth: '500px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        margin: '1rem'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ☁️ Connect to Salesforce
          </h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--blue-50)',
            border: '1px solid var(--blue-200)',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--blue-800)' }}>
              ℹ️ You'll need a Salesforce Connected App with OAuth settings configured.
              <a 
                href="https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm" 
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: '0.5rem', color: 'var(--blue-600)' }}
              >
                Learn More →
              </a>
            </p>
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Consumer Key (Client ID)
            </label>
            <input
              type="text"
              value={credentials.client_id}
              onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
              placeholder="Enter Consumer Key"
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
              From your Salesforce Connected App settings
            </p>
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Consumer Secret (Client Secret)
            </label>
            <input
              type="password"
              value={credentials.client_secret}
              onChange={(e) => setCredentials({ ...credentials, client_secret: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
              placeholder="Enter Consumer Secret"
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
              From your Salesforce Connected App settings
            </p>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Environment
            </label>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="environment"
                  checked={!credentials.is_sandbox}
                  onChange={() => setCredentials({ ...credentials, is_sandbox: false })}
                  style={{ marginRight: '0.5rem' }}
                />
                Production
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="environment"
                  checked={credentials.is_sandbox}
                  onChange={() => setCredentials({ ...credentials, is_sandbox: true })}
                  style={{ marginRight: '0.5rem' }}
                />
                Sandbox
              </label>
            </div>
          </div>
        </div>
        <div style={{ 
          padding: '1rem 1.5rem', 
          borderTop: '1px solid var(--gray-200)', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '0.5rem' 
        }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary" 
            onClick={() => onConnect(credentials)}
            disabled={!credentials.client_id || !credentials.client_secret}
          >
            Connect
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Field Mapping Modal
// ============================================================================

const FieldMappingModal = ({ objectType, mappings, availableFields, salesforceFields, onSave, onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ 
        width: '100%', 
        maxWidth: '800px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        margin: '1rem'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'capitalize' }}>
            🔄 {objectType} Field Mappings
          </h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <FieldMappingEditor
            mappings={mappings}
            availableFields={availableFields}
            salesforceFields={salesforceFields}
            onSave={onSave}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const SalesforceIntegration = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncConfig, setSyncConfig] = useState({
    leads: { enabled: true, direction: 'bidirectional', frequency: 'realtime', create_new: true, update_existing: true },
    contacts: { enabled: true, direction: 'bidirectional', frequency: 'realtime', create_new: true, update_existing: true },
    accounts: { enabled: false, direction: 'to_salesforce', frequency: 'daily', create_new: false, update_existing: true },
    opportunities: { enabled: false, direction: 'from_salesforce', frequency: 'hourly', create_new: false, update_existing: true },
  });
  const [fieldMappings, setFieldMappings] = useState({
    leads: [],
    contacts: [],
    accounts: [],
    opportunities: []
  });
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncStats, setSyncStats] = useState(null);
  const [salesforceFields, setSalesforceFields] = useState({
    leads: [],
    contacts: [],
    accounts: [],
    opportunities: []
  });
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [selectedObjectType, setSelectedObjectType] = useState('leads');
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Available Sales Radar fields for mapping
  const salesRadarFields = {
    leads: ['email', 'first_name', 'last_name', 'company', 'title', 'phone', 'website', 'industry', 'lead_source', 'description', 'status', 'score', 'city', 'state', 'country'],
    contacts: ['email', 'first_name', 'last_name', 'title', 'phone', 'mobile_phone', 'department', 'description'],
    accounts: ['name', 'website', 'phone', 'industry', 'description', 'type', 'annual_revenue', 'number_of_employees'],
    opportunities: ['name', 'amount', 'stage', 'close_date', 'probability', 'description', 'type', 'lead_source']
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch connection status
  const fetchConnectionStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/crm/salesforce/status');
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch connection status:', error);
      setConnectionStatus({ connected: false });
    }
  }, []);

  // Fetch sync configuration
  const fetchSyncConfig = useCallback(async () => {
    try {
      const response = await api.get('/api/crm/salesforce/config');
      if (response.data) {
        setSyncConfig(response.data.sync_settings || syncConfig);
        setFieldMappings(response.data.field_mappings || fieldMappings);
      }
    } catch (error) {
      console.error('Failed to fetch sync config:', error);
    }
  }, []);

  // Fetch Salesforce field metadata
  const fetchSalesforceFields = useCallback(async () => {
    if (!connectionStatus?.connected) return;
    
    try {
      const objectTypes = ['Lead', 'Contact', 'Account', 'Opportunity'];
      const fieldsPromises = objectTypes.map(async (objType) => {
        try {
          const response = await api.get(`/api/crm/salesforce/describe/${objType}`);
          return { type: objType.toLowerCase() + 's', fields: response.data.fields || [] };
        } catch {
          return { type: objType.toLowerCase() + 's', fields: [] };
        }
      });
      
      const results = await Promise.all(fieldsPromises);
      const newFields = {};
      results.forEach(r => {
        newFields[r.type] = r.fields;
      });
      setSalesforceFields(newFields);
    } catch (error) {
      console.error('Failed to fetch Salesforce fields:', error);
    }
  }, [connectionStatus?.connected]);

  // Fetch sync history
  const fetchSyncHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/api/crm/salesforce/sync/history');
      setSyncHistory(response.data.history || []);
      setSyncStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch sync history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchConnectionStatus();
      await fetchSyncConfig();
      setLoading(false);
    };
    loadData();
  }, [fetchConnectionStatus, fetchSyncConfig]);

  // Load Salesforce fields when connected
  useEffect(() => {
    if (connectionStatus?.connected) {
      fetchSalesforceFields();
      fetchSyncHistory();
    }
  }, [connectionStatus?.connected, fetchSalesforceFields, fetchSyncHistory]);

  // Connect to Salesforce
  const handleConnect = async (credentials) => {
    try {
      const response = await api.post('/api/crm/salesforce/connect', {
        client_id: credentials.client_id,
        client_secret: credentials.client_secret,
        is_sandbox: credentials.is_sandbox,
        redirect_uri: `${window.location.origin}/integrations/salesforce/callback`
      });
      
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to connect to Salesforce', 'error');
    }
  };

  // Disconnect from Salesforce
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Salesforce?')) return;
    
    try {
      await api.post('/api/crm/salesforce/disconnect');
      setConnectionStatus({ connected: false });
      showToast('Disconnected from Salesforce');
    } catch (error) {
      showToast('Failed to disconnect', 'error');
    }
  };

  // Save sync configuration
  const handleSaveConfig = async () => {
    try {
      await api.put('/api/crm/salesforce/config', {
        sync_settings: syncConfig,
        field_mappings: fieldMappings
      });
      showToast('Configuration saved successfully');
    } catch (error) {
      showToast('Failed to save configuration', 'error');
    }
  };

  // Trigger manual sync
  const handleSync = async (objectType = null, direction = 'bidirectional') => {
    setSyncing(true);
    try {
      const payload = {
        direction,
        object_type: objectType
      };
      
      const response = await api.post('/api/crm/salesforce/sync', payload);
      
      showToast(`Sync completed: ${response.data.synced || 0} records processed`);
      fetchSyncHistory();
    } catch (error) {
      showToast(error.response?.data?.error || 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Handle field mapping save
  const handleSaveMappings = (mappings) => {
    setFieldMappings({
      ...fieldMappings,
      [selectedObjectType]: mappings
    });
    setMappingModalOpen(false);
    showToast('Field mappings updated. Save configuration to apply.', 'info');
  };

  // Test connection
  const handleTestConnection = async () => {
    try {
      const response = await api.post('/api/crm/salesforce/test');
      if (response.data.success) {
        showToast(`Connection successful! User: ${response.data.username}`);
      }
    } catch (error) {
      showToast('Connection test failed', 'error');
    }
  };

  const tabs = [
    { label: 'Sync Settings', icon: '⚙️' },
    { label: 'Field Mappings', icon: '🔄' },
    { label: 'Sync History', icon: '📋' },
    { label: 'Manual Sync', icon: '☁️' }
  ];

  if (loading) {
    return (
      <div>
        <div style={{ 
          height: '4px', 
          backgroundColor: 'var(--gray-200)', 
          borderRadius: '2px', 
          overflow: 'hidden',
          marginBottom: '1rem'
        }}>
          <div style={{
            height: '100%',
            width: '30%',
            backgroundColor: 'var(--primary-600)',
            animation: 'progress 1s ease-in-out infinite'
          }} />
        </div>
        <p style={{ textAlign: 'center', color: 'var(--gray-600)' }}>Loading Salesforce integration...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SalesforceLogo />
            <div>
              <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: 'var(--gray-900)' }}>
                Salesforce Integration
              </h1>
              <p style={{ color: 'var(--gray-600)', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                Sync your leads, contacts, accounts, and opportunities with Salesforce CRM
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              border: `1px solid ${connectionStatus?.connected ? 'var(--green-300)' : 'var(--red-300)'}`,
              backgroundColor: connectionStatus?.connected ? 'var(--green-50)' : 'var(--red-50)',
              color: connectionStatus?.connected ? 'var(--green-700)' : 'var(--red-700)'
            }}>
              {connectionStatus?.connected ? '✅ Connected' : '❌ Not Connected'}
            </span>
            {connectionStatus?.connected ? (
              <button className="btn btn-danger" onClick={handleDisconnect}>
                🔌 Disconnect
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => setConnectModalOpen(true)}>
                🔗 Connect
              </button>
            )}
          </div>
        </div>
        
        {connectionStatus?.connected && (
          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.25rem'
            }}>
              User: {connectionStatus.username || 'Unknown'}
            </span>
            <span style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.25rem'
            }}>
              Org: {connectionStatus.organization_id || 'Unknown'}
            </span>
            <span style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              border: `1px solid ${connectionStatus.is_sandbox ? 'var(--yellow-300)' : 'var(--blue-300)'}`,
              backgroundColor: connectionStatus.is_sandbox ? 'var(--yellow-50)' : 'var(--blue-50)',
              color: connectionStatus.is_sandbox ? 'var(--yellow-700)' : 'var(--blue-700)',
              borderRadius: '0.25rem'
            }}>
              {connectionStatus.is_sandbox ? '🧪 Sandbox' : '🏭 Production'}
            </span>
          </div>
        )}
      </div>

      {/* Connection Required Alert */}
      {!connectionStatus?.connected && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--blue-50)',
          border: '1px solid var(--blue-200)',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ margin: 0, color: 'var(--blue-800)' }}>
            ℹ️ Connect your Salesforce account to enable data synchronization. You'll need your
            Salesforce Connected App credentials (Client ID and Client Secret).
          </p>
        </div>
      )}

      {/* Main Content - Only show when connected */}
      {connectionStatus?.connected && (
        <>
          {/* Tabs */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ 
              display: 'flex', 
              borderBottom: '1px solid var(--gray-200)',
              overflowX: 'auto'
            }}>
              {tabs.map((tab, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  style={{
                    padding: '1rem 1.5rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: activeTab === idx ? 'var(--primary-600)' : 'var(--gray-600)',
                    borderBottom: activeTab === idx ? '2px solid var(--primary-600)' : '2px solid transparent',
                    fontWeight: activeTab === idx ? 500 : 400,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 0 && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Object Sync Configuration
                </h2>
                <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Configure which Salesforce objects to sync and how.
                </p>
              </div>
              
              {['leads', 'contacts', 'accounts', 'opportunities'].map((objType) => (
                <ObjectSyncConfig
                  key={objType}
                  config={syncConfig[objType]}
                  onChange={(config) => setSyncConfig({ ...syncConfig, [objType]: config })}
                  objectType={objType}
                />
              ))}
              
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button className="btn" onClick={handleTestConnection}>
                  🧪 Test Connection
                </button>
                <button className="btn btn-primary" onClick={handleSaveConfig}>
                  ✅ Save Configuration
                </button>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Field Mappings
                </h2>
                <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Configure how Sales Radar fields map to Salesforce fields.
                </p>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                {['leads', 'contacts', 'accounts', 'opportunities'].map((objType) => (
                  <div key={objType} className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{getObjectIcon(objType)}</span>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, textTransform: 'capitalize' }}>
                        {objType}
                      </h3>
                    </div>
                    <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                      {fieldMappings[objType]?.length || 0} field mappings configured
                    </p>
                    <button
                      className="btn"
                      onClick={() => {
                        setSelectedObjectType(objType);
                        setMappingModalOpen(true);
                      }}
                    >
                      ✏️ Edit Mappings
                    </button>
                  </div>
                ))}
              </div>
              
              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={handleSaveConfig}>
                  ✅ Save All Mappings
                </button>
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    Sync History
                  </h2>
                  <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', margin: 0 }}>
                    View recent synchronization activity
                  </p>
                </div>
                <button className="btn" onClick={fetchSyncHistory} disabled={historyLoading}>
                  🔄 Refresh
                </button>
              </div>
              
              {/* Stats Cards */}
              {syncStats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  {[
                    { label: 'Total Syncs', value: syncStats.total_syncs || 0 },
                    { label: 'Records Synced', value: syncStats.total_records || 0 },
                    { label: 'Last Sync', value: syncStats.last_sync ? new Date(syncStats.last_sync).toLocaleString() : 'Never', isText: true },
                    { label: 'Success Rate', value: syncStats.success_rate ? `${syncStats.success_rate}%` : 'N/A', isGreen: true }
                  ].map((stat, idx) => (
                    <div key={idx} className="card" style={{ padding: '1rem' }}>
                      <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', margin: 0 }}>
                        {stat.label}
                      </p>
                      <p style={{ 
                        fontSize: stat.isText ? '0.875rem' : '1.5rem', 
                        fontWeight: 600, 
                        color: stat.isGreen ? 'var(--green-600)' : 'var(--gray-900)', 
                        margin: '0.5rem 0 0 0' 
                      }}>
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <SyncHistory history={syncHistory} loading={historyLoading} />
              </div>
            </div>
          )}

          {activeTab === 3 && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Manual Sync
                </h2>
                <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Trigger manual synchronization between Sales Radar and Salesforce.
                </p>
              </div>
              
              {/* Full Sync */}
              <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '2.5rem' }}>☁️</span>
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>
                      Full Synchronization
                    </h3>
                    <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', margin: 0 }}>
                      Sync all enabled objects in both directions
                    </p>
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => handleSync(null, 'bidirectional')}
                  disabled={syncing}
                >
                  {syncing ? '⏳ Syncing...' : '🔄 Start Full Sync'}
                </button>
              </div>
              
              {/* Object-specific sync */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                {['leads', 'contacts', 'accounts', 'opportunities'].map((objType) => (
                  <div key={objType} className="card" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{getObjectIcon(objType)}</span>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, textTransform: 'capitalize' }}>
                        {objType}
                      </h3>
                      {syncConfig[objType]?.enabled && (
                        <span style={{
                          padding: '0.125rem 0.5rem',
                          fontSize: '0.75rem',
                          backgroundColor: 'var(--green-100)',
                          color: 'var(--green-700)',
                          borderRadius: '9999px'
                        }}>
                          Enabled
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        className="btn"
                        style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                        onClick={() => handleSync(objType, 'to_salesforce')}
                        disabled={syncing || !syncConfig[objType]?.enabled}
                      >
                        → To SF
                      </button>
                      <button
                        className="btn"
                        style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                        onClick={() => handleSync(objType, 'from_salesforce')}
                        disabled={syncing || !syncConfig[objType]?.enabled}
                      >
                        ← From SF
                      </button>
                      <button
                        className="btn"
                        style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                        onClick={() => handleSync(objType, 'bidirectional')}
                        disabled={syncing || !syncConfig[objType]?.enabled}
                      >
                        ⇄ Both
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Connect Modal */}
      {connectModalOpen && (
        <ConnectModal
          onClose={() => setConnectModalOpen(false)}
          onConnect={(creds) => {
            handleConnect(creds);
            setConnectModalOpen(false);
          }}
        />
      )}

      {/* Field Mapping Modal */}
      {mappingModalOpen && (
        <FieldMappingModal
          objectType={selectedObjectType}
          mappings={fieldMappings[selectedObjectType] || []}
          availableFields={salesRadarFields[selectedObjectType] || []}
          salesforceFields={salesforceFields[selectedObjectType] || []}
          onSave={handleSaveMappings}
          onClose={() => setMappingModalOpen(false)}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SalesforceIntegration;
